import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LiveKitVideo } from '@/features/webcamStream';

/**
 * Test page for LiveKit video streaming functionality
 */
export const LiveKitTestPage: React.FC = () => {
  const [roomName, setRoomName] = useState('test-room-123');
  const [participantName, setParticipantName] = useState('test-user');
  const [mode, setMode] = useState<'stream' | 'join'>('stream');
  const [isActive, setIsActive] = useState(false);

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading>LiveKit Video Streaming Test</Heading>

        {/* Configuration Form */}
        <Box p={6} bg="gray.50" borderRadius="md">
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Room Name</FormLabel>
              <Input
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="Enter room name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Participant Name</FormLabel>
              <Input
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                placeholder="Enter participant name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Mode</FormLabel>
              <Select value={mode} onChange={(e) => setMode(e.target.value as 'stream' | 'join')}>
                <option value="stream">Stream (Broadcast)</option>
                <option value="join">View (Watch Only)</option>
              </Select>
            </FormControl>

            <Button
              colorScheme={isActive ? 'red' : 'green'}
              onClick={() => setIsActive(!isActive)}
              size="lg"
              w="full"
            >
              {isActive ? 'Stop' : 'Start'} {mode === 'stream' ? 'Streaming' : 'Viewing'}
            </Button>
          </VStack>
        </Box>

        {/* LiveKit Video Component */}
        {isActive && (
          <Box p={4} bg="blue.50" borderRadius="md">
            <Text fontWeight="bold" mb={2}>
              LiveKit Video Component:
            </Text>
            <Text fontSize="sm" mb={4}>
              This component will automatically show a PiP window when streaming.
            </Text>
            <LiveKitVideo roomName={roomName} participantName={participantName} mode={mode} />
          </Box>
        )}

        {/* Debug Info */}
        <Box p={4} bg="yellow.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>
            Debug Information:
          </Text>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm">
              <strong>Room:</strong> {roomName}
            </Text>
            <Text fontSize="sm">
              <strong>Participant:</strong> {participantName}
            </Text>
            <Text fontSize="sm">
              <strong>Mode:</strong> {mode}
            </Text>
            <Text fontSize="sm">
              <strong>Status:</strong> {isActive ? 'Active' : 'Inactive'}
            </Text>
            <Text fontSize="sm" color="gray.600">
              Check browser console for detailed logs
            </Text>
          </VStack>
        </Box>

        {/* Instructions */}
        <Box p={4} bg="green.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>
            Test Instructions:
          </Text>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm">
              1. <strong>Set room name</strong> and participant name
            </Text>
            <Text fontSize="sm">
              2. <strong>Choose mode:</strong> Stream (broadcast) or Join (view)
            </Text>
            <Text fontSize="sm">
              3. <strong>Click Start</strong> to activate the component
            </Text>
            <Text fontSize="sm">
              4. <strong>Check console</strong> for debug logs
            </Text>
            <Text fontSize="sm">
              5. <strong>Verify PiP:</strong> Should appear automatically
            </Text>
            <Text fontSize="sm">
              6. <strong>Test modes:</strong> Streamer should be mini, viewer should be toggleable
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};
